import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import DocumentTitleSync from './components/routing/DocumentTitleSync';
import { RouteErrorPage } from './components/routing/RouteStatusPages';
import { lazyPage } from './utils/lazyRoute';
import AppLayout from './layouts/AppLayout';
import ProductsLayout from './layouts/ProductsLayout';
import StudioLayout from './layouts/StudioLayout';
import AdminLayout from './layouts/AdminLayout';
import { LegacyUrlRedirect } from './components/routing/LegacyUrlRedirect';
import ProductListPage from './pages/products/ProductListPage';
import AmazonListingsPage from './pages/products/AmazonListingsPage';
import AsinSearchPage from './pages/products/AsinSearchPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import ManualProductListPage from './pages/products/ManualProductListPage';
import ManualProductUploadPage from './pages/products/ManualProductUploadPage';
import ManualProductDetailPage from './pages/products/ManualProductDetailPage';
import ManualProductNewPage from './pages/products/ManualProductNewPage';
import AmazonAuthPage from './pages/amazon/AmazonAuthPage';
import AmazonAdsAuthPage from './pages/amazon/AmazonAdsAuthPage';
import BrandListPage from './pages/brands/BrandListPage';
import BrandDetailPage from './pages/brands/BrandDetailPage';
import TrashPage from './pages/TrashPage';
import DashboardPage from './pages/DashboardPage';
import SupportMeetingPage from './pages/SupportMeetingPage';
import ConnectionsPage from './pages/connections/ConnectionsPage';
import MaterialsBrowsePage from './pages/MaterialsBrowsePage';
import TagsManagementPage from './pages/TagsManagementPage';
import UserManagementPage from './pages/UserManagementPage';
import OrganizationManagementPage from './pages/OrganizationManagementPage';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
import { DeclineInvitationPage } from './pages/DeclineInvitationPage';
import ProfilePage from './pages/ProfilePage';
import { ConfirmEmailChangePage } from './pages/ConfirmEmailChangePage';
import OrganizationJoinPage from './pages/organizations/OrganizationJoinPage';
import JoinRequestsPage from './pages/organizations/JoinRequestsPage';
import OnboardingPage from './pages/organizations/OnboardingPage';
import OrganizationSetupPage from './pages/organizations/OrganizationSetupPage';
import CreateOrganizationPage from './pages/organizations/CreateOrganizationPage';
import OrganizationSearchPage from './pages/organizations/OrganizationSearchPage';
import OrganizationCreatedPage from './pages/organizations/OrganizationCreatedPage';
import OrganizationJoinRequestedPage from './pages/organizations/OrganizationJoinRequestedPage';
import InvitationAcceptedPage from './pages/organizations/InvitationAcceptedPage';
import PaymentCompletePage from './pages/PaymentCompletePage';
import { CanvaAuthCompletePage } from './pages/CanvaAuthCompletePage';
import { YouTubeAuthCompletePage } from './pages/YouTubeAuthCompletePage';
import OrgAdminLayout from './layouts/OrgAdminLayout';
import GoogleAdsAuthPage from './pages/connections/GoogleAdsAuthPage';
import CreatorProfilePage from './pages/CreatorProfilePage';
import ExclusiveActorsPage from './pages/ExclusiveActorsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import HomePage from './pages/HomePage';
import Take8HomePage from './pages/Take8HomePage';
import UsodekiHomePage from './pages/UsodekiHomePage';
import HypeAppHomePage from './pages/home/HypeAppHomePage';
import TermsPage from './pages/landing-page/TermsPage';
import ManualPage from './manual/ManualPage';
import PartnerManualPage from './manual/PartnerManualPage';
import {
  EmailSignupPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  EmailConfirmPage,
  SignupSelectPage,
} from './pages/auth';
import { brandConfig } from './config/brands';

// admin / org-admin の管理画面ページや動画エディタは一般ユーザーの初回バンドルに載せない。
// lazyPage はデプロイ直後の古いチャンク読み込み失敗を 1 回のリロードで吸収する (utils/lazyRoute.ts)。

function ProductsTabRedirect() {
  const lastTab = localStorage.getItem('products_last_tab');
  const useAmazon = brandConfig.features.amazonIntegration && lastTab === '/products/amazon';
  return <Navigate to={useAmazon ? '/products/amazon' : '/products/manual'} replace />;
}

// 各ルートの handle: { title, titleSuffix } が document.title の定義元。
// 解決は DocumentTitleSync が担う (優先度・合成規則はそちらのコメントを参照)。
// 商品名など実行時にしか決まらないタイトルは、ページ側で useDocumentTitle() を使う。
const appRoutes: RouteObject[] = [
  // 認証ページ（AuthGate外）
  {
    path: '/auth/signup',
    element: <SignupSelectPage />,
    handle: { title: '新規登録' },
  },
  {
    path: '/auth/email/login',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/auth/email/signup',
    element: <EmailSignupPage />,
    handle: { title: 'メールアドレスで新規登録' },
  },
  {
    path: '/auth/email/forgot-password',
    element: <ForgotPasswordPage />,
    handle: { title: 'パスワードをお忘れの方' },
  },
  {
    path: '/auth/email/reset-password',
    element: <ResetPasswordPage />,
    handle: { title: 'パスワード再設定' },
  },
  {
    path: '/auth/email/confirm',
    element: <EmailConfirmPage />,
    handle: { title: 'メールアドレスの確認' },
  },
  {
    path: '/accept-invitation/:token',
    element: <AcceptInvitationPage />,
    handle: { title: '招待の承認' },
  },
  {
    path: '/decline-invitation/:token',
    element: <DeclineInvitationPage />,
    handle: { title: '招待の辞退' },
  },
  {
    path: '/confirm-email-change/:token',
    element: <ConfirmEmailChangePage />,
    handle: { title: 'メールアドレス変更の確認' },
  },
  {
    path: '/org/:slug',
    element: <OrganizationJoinPage />,
    handle: { title: '組織に参加' },
  },
  {
    path: '/onboarding',
    element: <OnboardingPage />,
    handle: { title: '初期設定' },
  },
  {
    path: '/organizations/setup',
    element: <OrganizationSetupPage />,
    handle: { title: '初期設定' },
  },
  {
    path: '/organizations/create',
    element: <CreateOrganizationPage />,
    handle: { title: '組織を作成' },
  },
  {
    path: '/organizations/search',
    element: <OrganizationSearchPage />,
    handle: { title: '組織を探す' },
  },
  {
    path: '/organizations/created',
    element: <OrganizationCreatedPage />,
    handle: { title: '組織を作成しました' },
  },
  {
    path: '/organizations/join-requested',
    element: <OrganizationJoinRequestedPage />,
    handle: { title: '参加申請を送信しました' },
  },
  {
    path: '/organizations/invitation-accepted',
    element: <InvitationAcceptedPage />,
    handle: { title: '招待を承認しました' },
  },
  {
    path: '/payment-complete',
    element: <PaymentCompletePage />,
    handle: { title: 'お支払い完了' },
  },
  {
    path: '/canva/auth-complete',
    element: <CanvaAuthCompletePage />,
    handle: { title: 'Canva 連携' },
  },
  {
    path: '/youtube/auth-complete',
    element: <YouTubeAuthCompletePage />,
    handle: { title: 'YouTube 連携' },
  },
  {
    path: '/lp/terms',
    element: <TermsPage />,
    handle: { title: '利用規約' },
  },
  // マニュアル（別ウィンドウ表示、認証不要）
  {
    path: '/manual',
    element: <ManualPage />,
  },
  // ACP クリエイター公開プロフィール (未ログイン閲覧可・AuthGate PUBLIC_PATHS)
  {
    path: '/creators/:slug',
    element: <CreatorProfilePage />,
    handle: { title: 'クリエイター' },
  },
  {
    path: '/manual/:section/:slug',
    element: <ManualPage />,
  },
  // パートナー組織向けマニュアル（/org-admin/* と並列のルート。
  // OrgAdminLayout は経由せず、partner admin もしくは grant 保有 designer のみアクセス可）
  ...(brandConfig.features.oemAttributeManagement ? [
    {
      path: '/org-admin/manual',
      element: <PartnerManualPage />,
    },
    {
      path: '/org-admin/manual/:section/:slug',
      element: <PartnerManualPage />,
    },
  ] : []),
  // OEM組織管理者用の管理画面（LightMedia用/admin とは独立）
  // 有効化はブランド設定の oemAttributeManagement フラグで制御する
  ...(brandConfig.features.oemAttributeManagement ? [{
    path: '/org-admin',
    element: <OrgAdminLayout />,
    handle: { titleSuffix: `${brandConfig.serviceName} 管理画面` },
    children: [
      { index: true, element: <Navigate to="/org-admin/reports" replace /> },
      { path: 'attributes', lazy: lazyPage(() => import('./pages/org-admin/OrgAttributeManagementPage')), handle: { title: '静止画 属性管理' } },
      { path: 'actors', lazy: lazyPage(() => import('./pages/org-admin/OrgActorManagementPage')), handle: { title: 'AI俳優管理' } },
      { path: 'sound-effects', lazy: lazyPage(() => import('./pages/org-admin/OrgSoundEffectsPage')), handle: { title: '効果音管理' } },
      { path: 'free-bgm-tracks', lazy: lazyPage(() => import('./pages/org-admin/OrgFreeBgmTracksPage')), handle: { title: 'フリー楽曲管理' } },
      // 旧トーン管理は属性ツリー(scenario-attributes)へ統合。既存リンクはリダイレクト。
      { path: 'scenario-options', element: <Navigate to="/org-admin/scenario-attributes" replace /> },
      { path: 'scenario-tone', element: <Navigate to="/org-admin/scenario-attributes" replace /> },
      { path: 'scenario-attributes', lazy: lazyPage(() => import('./pages/org-admin/OrgScenarioAttributeManagementPage')), handle: { title: '動画シナリオ設定' } },
      { path: 'scenario-other', lazy: lazyPage(() => import('./pages/org-admin/OrgScenarioOtherSettingsPage')), handle: { title: '動画 その他の設定' } },
      { path: 'media', lazy: lazyPage(() => import('./pages/org-admin/OrgMediaManagementPage')), handle: { title: '静止画 媒体管理' } },
      { path: 'announcements', lazy: lazyPage(() => import('./pages/org-admin/OrgAnnouncementsPage')), handle: { title: 'お知らせ管理' } },
      { path: 'site-settings', lazy: lazyPage(() => import('./pages/org-admin/SiteSettingsPage')), handle: { title: 'サイト設定' } },
      { path: 'connections', lazy: lazyPage(() => import('./pages/org-admin/OrgConnectionsPage')), handle: { title: '外部サービス接続' } },
      { path: 'theme', element: <Navigate to="/org-admin/site-settings" replace /> },
      { path: 'plans', lazy: lazyPage(() => import('./pages/org-admin/OrgPlansPage')), handle: { title: 'プラン' } },
      { path: 'reports', lazy: lazyPage(() => import('./pages/org-admin/ReportsPage')), handle: { title: '収支レポート' } },
      { path: 'payment-report', lazy: lazyPage(() => import('./pages/org-admin/PaymentReportPage')), handle: { title: '決済レポート' } },
      { path: 'funnel', lazy: lazyPage(() => import('./pages/org-admin/FunnelReportPage')), handle: { title: 'ファネル分析' } },
      { path: 'usage-report', lazy: lazyPage(() => import('./pages/org-admin/UsageReportPage')), handle: { title: '利用状況レポート' } },
      { path: 'organizations', lazy: lazyPage(() => import('./pages/org-admin/OrgOrganizationsPage')), handle: { title: '組織管理' } },
      { path: 'organizations/:orgId', lazy: lazyPage(() => import('./pages/org-admin/OrgOrganizationDetailPage')), handle: { title: '組織詳細' } },
      { path: 'users', lazy: lazyPage(() => import('./pages/org-admin/OrgUsersPage')), handle: { title: 'ユーザー管理' } },
      { path: 'users/:userUid', lazy: lazyPage(() => import('./pages/org-admin/OrgUserDetailPage')), handle: { title: 'ユーザー詳細' } },
      { path: 'materials', lazy: lazyPage(() => import('./pages/org-admin/OrgMaterialsPage')), handle: { title: '素材監査' } },
      { path: 'scenarios', lazy: lazyPage(() => import('./pages/org-admin/OrgScenariosPage')), handle: { title: 'シナリオ監査' } },
      // バナープロンプト閲覧 (LightMedia 限定。ページ内で isPlatformOrg ガード)
      { path: 'banner-prompts', lazy: lazyPage(() => import('./pages/org-admin/OrgBannerPromptPage')), handle: { title: 'バナープロンプト' } },
      { path: 'impersonation', lazy: lazyPage(() => import('./pages/org-admin/OrgImpersonationAuditPage')), handle: { title: '代行アクセス監査' } },
      // 代行権限管理は組織情報詳細・ユーザー詳細へ統合済み（独立ページ破棄）。
      // 旧URL /org-admin/impersonation-grants は未定義となり catch-all (* → /) でホームへ。
      // ミーティング管理・ポイント調整は組織情報詳細へ統合済み。旧URLは一覧へリダイレクト（当面）
      { path: 'meetings', element: <Navigate to="/org-admin/organizations" replace /> },
      { path: 'credits', element: <Navigate to="/org-admin/organizations" replace /> },
    ],
  }] : []),
  {
    path: '/',
    element: <AppLayout />,
    children: [
      // トップページ（旧URLパラメータがある場合はリダイレクト）
      {
        index: true,
        element: <LegacyUrlRedirect fallback={
          brandConfig.homePageVariant === 'hypeapp' ? <HypeAppHomePage />
          : brandConfig.homePageVariant === 'take8' ? <Take8HomePage />
          : brandConfig.homePageVariant === 'usodeki' ? <UsodekiHomePage />
          : <HomePage />
        } />,
      },
      // 生成スタジオ
      {
        path: 'studio',
        element: <StudioLayout />,
        handle: { title: brandConfig.labels.studio },
        children: [
          { index: true, element: null }, // GenerationWorkspaceがタブを管理
          { path: 'image', element: null, handle: { title: '静止画生成' } },
          { path: 'image/prompt', element: null, handle: { title: '静止画生成 (プロンプト)' } },
          { path: 'image/purpose', element: null, handle: { title: '静止画生成 (目的から選ぶ)' } },
          { path: 'video', element: null, handle: { title: '動画生成' } },
          { path: 'video/prompt', element: null, handle: { title: '動画生成 (プロンプト)' } },
          // シナリオ名はページ側で useDocumentTitle() が上書きする
          { path: 'video/:scenarioId', element: null, handle: { title: '動画生成' } },
        ],
      },
      // 動画エディタ (プレビュー編集・platform admin 限定。ページ内で isPlatformOrg && isAdmin ガード)。
      // remotion をメインバンドルから分離するため lazy (dynamic import) で追加する。
      {
        path: 'studio/video/:scenarioId/editor/:projectId?',
        lazy: lazyPage(() => import('./features/videoEditor/VideoEditorPage')),
        handle: { title: '動画エディタ' },
      },
      // 素材起点 (シナリオなし) の編集プロジェクト (docs/features/video-editor-presentation-import-plan.md §4.1)。
      // 同じ VideoEditorPage が scenarioId 省略時に project.origin で分岐する。
      {
        path: 'studio/video/editor/:projectId',
        lazy: lazyPage(() => import('./features/videoEditor/VideoEditorPage')),
        handle: { title: '動画エディタ' },
      },
      // 素材管理
      {
        path: 'materials',
        children: [
          { index: true, element: <Navigate to="/materials/shared" replace /> },
          {
            path: 'shared',
            element: (
              <ProductsLayout>
                <MaterialsBrowsePage defaultFolder="shared" />
              </ProductsLayout>
            ),
            handle: { title: `共有フォルダの${brandConfig.labels.materials}` },
          },
          {
            path: 'personal',
            element: (
              <ProductsLayout>
                <MaterialsBrowsePage defaultFolder="personal" />
              </ProductsLayout>
            ),
            handle: { title: `個人フォルダの${brandConfig.labels.materials}` },
          },
        ],
      },
      {
        path: 'dashboard',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <DashboardPage />, handle: { title: 'ダッシュボード' } },
        ],
      },
      {
        path: 'products',
        element: <ProductsLayout />,
        children: [
          { index: true, element: brandConfig.features.amazonIntegration ? <ProductsTabRedirect /> : <Navigate to="/products/manual" replace /> },
          ...(brandConfig.features.amazonIntegration ? [
            { path: 'amazon', element: <ProductListPage />, handle: { title: `Amazon${brandConfig.labels.products}` } },
            { path: 'amazon-listings', element: <AmazonListingsPage />, handle: { title: 'Amazon出品情報' } },
            { path: 'asin-search', element: <AsinSearchPage />, handle: { title: 'ASIN検索' } },
          ] : []),
          { path: ':platform/:identifier', element: <ProductDetailPage />, handle: { title: brandConfig.labels.product } },
          { path: 'manual', element: <ManualProductListPage />, handle: { title: brandConfig.labels.products } },
          { path: 'manual/new', element: <ManualProductNewPage />, handle: { title: `${brandConfig.labels.product}を登録` } },
          { path: 'manual/upload', element: <ManualProductUploadPage />, handle: { title: `${brandConfig.labels.product}を登録` } },
          { path: 'manual/:productId', element: <ManualProductDetailPage />, handle: { title: brandConfig.labels.product } },
        ],
      },
      {
        path: 'brands',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <BrandListPage />, handle: { title: brandConfig.labels.brandManagement } },
          { path: 'new', element: <BrandDetailPage />, handle: { title: `${brandConfig.labels.brand}を作成` } },
          { path: ':id', element: <BrandDetailPage />, handle: { title: brandConfig.labels.brand } },
        ],
      },
      // 接続管理は Amazon ゲートに依存させない (Google 広告等、ブランド非依存の連携も含むため)。
      // 各 child route は対応する feature flag で個別にゲートする。
      {
        path: 'connections',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <ConnectionsPage />, handle: { title: '外部サービス連携' } },
          ...(brandConfig.features.amazonIntegration ? [
            { path: 'amazon', element: <AmazonAuthPage />, handle: { title: 'Amazon 連携' } },
            { path: 'amazon-ads', element: <AmazonAdsAuthPage />, handle: { title: 'Amazon広告 連携' } },
          ] : []),
          { path: 'google-ads', element: <GoogleAdsAuthPage />, handle: { title: 'Google広告 連携' } },
        ],
      },
      {
        path: 'trash',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <TrashPage />, handle: { title: 'ゴミ箱' } },
        ],
      },
      {
        path: 'tags',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <TagsManagementPage />, handle: { title: brandConfig.labels.tagManagement } },
        ],
      },
      {
        path: 'exclusive-actors',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <ExclusiveActorsPage />, handle: { title: `専属${brandConfig.labels.aiActors}` } },
        ],
      },
      {
        path: 'users',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <UserManagementPage />, handle: { title: `${brandConfig.labels.members}管理` } },
        ],
      },
      {
        path: 'organizations',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <OrganizationManagementPage />, handle: { title: `${brandConfig.labels.organization}管理` } },
          { path: 'join-requests', element: <JoinRequestsPage />, handle: { title: '参加申請' } },
        ],
      },
      {
        path: 'profile',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <ProfilePage />, handle: { title: 'プロフィール' } },
        ],
      },
      {
        path: 'subscription',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <SubscriptionPage />, handle: { title: 'プラン・お支払い' } },
        ],
      },
      {
        path: 'support-meeting',
        element: <ProductsLayout />,
        children: [
          { index: true, element: <SupportMeetingPage />, handle: { title: 'サポートミーティング' } },
        ],
      },
      ...(brandConfig.features.platformAdmin ? [{
        path: 'admin',
        element: <AdminLayout />,
        // 自社プラットフォームの管理画面。OEM 各ブランドの画面ではないため
        // serviceName は使わず固定名にする。
        handle: { titleSuffix: 'Creative Platform管理画面' },
        children: [
          { index: true, element: <Navigate to="/admin/dashboard" replace /> },
          { path: 'dashboard', lazy: lazyPage(() => import('./pages/admin/AdminDashboardPage')), handle: { title: 'ダッシュボード' } },
          { path: 'actors', lazy: lazyPage(() => import('./pages/admin/ActorManagementPage')), handle: { title: brandConfig.labels.aiActors } },
          { path: 'seedance-preset-actors', lazy: lazyPage(() => import('./pages/admin/SeedancePresetActorsPage')), handle: { title: 'Seedance俳優' } },
          { path: 'sound-effects', lazy: lazyPage(() => import('./pages/admin/SoundEffectsPage')), handle: { title: '効果音カタログ' } },
          { path: 'free-bgm-tracks', lazy: lazyPage(() => import('./pages/admin/FreeBgmTracksPage')), handle: { title: 'フリー楽曲カタログ' } },
          { path: 'plans', lazy: lazyPage(() => import('./pages/admin/PlanManagementPage')), handle: { title: 'プラン' } },
          { path: 'credit-rates', lazy: lazyPage(() => import('./pages/admin/CreditRatesPage')), handle: { title: 'レート' } },
          { path: 'system', lazy: lazyPage(() => import('./pages/admin/SystemSettingsPage')), handle: { title: 'システム設定' } },
          { path: 'error-logs', lazy: lazyPage(() => import('./pages/admin/ErrorLogsPage')), handle: { title: 'エラーログ' } },
          { path: 'oem-billing', lazy: lazyPage(() => import('./pages/admin/OemBillingPage')), handle: { title: 'OEM利用状況' } },
          { path: 'acp-creators', lazy: lazyPage(() => import('./pages/admin/AcpCreatorsPage')), handle: { title: 'クリエイタープログラム' } },
          { path: 'acp-statements', lazy: lazyPage(() => import('./pages/admin/AcpStatementsPage')), handle: { title: 'クリエイターRS集計' } },
          { path: 'credit-adjustments', lazy: lazyPage(() => import('./pages/admin/CreditAdjustmentsMonitorPage')), handle: { title: 'ポイント調整 監視' } },
          { path: 'media-specs', lazy: lazyPage(() => import('./pages/admin/MediaSpecsPage')), handle: { title: '媒体マスタ' } },
        ],
      }] : []),
    ],
  },
  // 開発専用ハーネス (import.meta.env.DEV のみ登録。本番ビルドには含まれない)
  ...(import.meta.env.DEV ? [{
    path: '/__dev/purpose',
    lazy: async () => ({ Component: (await import('./dev/PurposeHarnessPage')).default }),
  }, {
    path: '/__dev/aspect-ratio',
    lazy: async () => ({ Component: (await import('./dev/AspectRatioPickerHarnessPage')).default }),
  }, {
    path: '/__dev/take8-home',
    lazy: async () => ({ Component: (await import('./dev/Take8HomeHarnessPage')).default }),
  }, {
    path: '/__dev/media-requirements',
    lazy: async () => ({ Component: (await import('./dev/MediaRequirementsHarnessPage')).default }),
  }, {
    path: '/__dev/prompt-history',
    lazy: async () => ({ Component: (await import('./dev/PromptHistoryHarnessPage')).default }),
  }, {
    path: '/__dev/toast',
    lazy: async () => ({ Component: (await import('./dev/ToastHarnessPage')).default }),
  }, {
    path: '/__dev/audit-highlight',
    lazy: async () => ({ Component: (await import('./dev/AuditHighlightHarnessPage')).default }),
  }] : []),
  // 存在しないURLはホームにリダイレクト（認証後のリダイレクトで404を防ぐ）
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

// 全ルートを DocumentTitleSync (path 無しのレイアウトルート) で包む。
// マッチ挙動には影響せず、Outlet を返すだけ。
// errorElement: 描画中の例外や lazy ルートの読み込み失敗を、既定の素のエラー画面ではなく
// RouteErrorPage (日本語案内 + 再読み込み) で受ける。
export const router = createBrowserRouter(
  [{ element: <DocumentTitleSync />, errorElement: <RouteErrorPage />, children: appRoutes }],
  { basename: import.meta.env.VITE_APP_BASENAME ?? '/' },
);


